import React, { FC, useCallback, useMemo, useState } from 'react';
import { Experiment, ExperimentClient, ExperimentUser } from '@amplitude/experiment-js-client';
import Amplitude from 'amplitude-js';

import { Spinner } from '../../components/Spinner';

import { AmplitudeFlagType, DefaultFlagValues } from './amplitudeFlags';
import { AmplitudeExperimentContext, AmplitudeExperimentContextValue } from './ExperimentContext';
import SegmentExposureTrackingProvider from './SegmentExposureTrackingProvider';
import useHandleLongRunningFetch from './useHandleLongRunningFetch';

export interface AmplitudeExperimentProviderProps {
  proId?: string;
  email?: string;
  CustomLoader?: () => React.JSX.Element;
}

const DEFAULT_EXPERIMENT_TRUTHY_VALUE = 'control';

export const createIsVariantActive = (reqEntity?: Record<string, unknown> | string, experiment?: ExperimentClient) => {
  return (variantId: AmplitudeFlagType, truthyExperimentValue?: string) => {
    if (!reqEntity || !experiment) {
      return false;
    }

    const variantObject = experiment.variant(variantId);

    if (truthyExperimentValue) {
      return variantObject.value === truthyExperimentValue;
    }

    return variantObject.value !== DEFAULT_EXPERIMENT_TRUTHY_VALUE;
  };
};

export const createGetVariantValue = (reqEntity?: Record<string, unknown> | string, experiment?: ExperimentClient) => {
  return (variantId: AmplitudeFlagType): string | null => {
    if (!reqEntity || !experiment) {
      return null;
    }

    const variantObject = experiment.variant(variantId);
    const { value } = variantObject;

    return value ?? null;
  };
};

export const createGetPayloadVariant = (
  reqEntity?: Record<string, unknown> | string,
  experiment?: ExperimentClient
) => {
  return <T,>(variantId: AmplitudeFlagType): T | null => {
    if (!reqEntity || !experiment) {
      return null;
    }
    const variantObject = experiment.variant(variantId);

    return variantObject.payload as T;
  };
};

const AnonymousExperimentProvider: FC<AmplitudeExperimentProviderProps> = ({
  children,
  proId,
  email,
  CustomLoader,
}) => {
  const debug = HC_ENV_NODE_ENV !== 'production';
  const isDevelopment = HC_ENV_APP_ENVIRONMENT !== 'production';
  const [isInitialized, setIsInitialized] = useState(false);

  const startExperiment = useCallback(
    async (experiment: ExperimentClient) => {
      if (!proId) {
        return;
      }

      const experimentUser: ExperimentUser = {
        user_id: proId,
        user_properties: {
          // premium: true,
          pro_uuid: proId,
          email: email || '',
        },
      };

      await experiment
        .fetch(experimentUser)
        .then((client) => {
          return client;
        })
        .finally(() => {
          setIsInitialized(true);
        });
    },
    [proId, email]
  );

  useHandleLongRunningFetch(isInitialized, () => setIsInitialized(true));

  const experiment = useMemo<ExperimentClient | undefined>(() => {
    if (!proId) {
      return undefined;
    }

    const { AMPLITUDE_ENDPOINT_URL = '' } = window;
    const AMPLITUDE_EXPERIMENT_JS_KEY = isDevelopment
      ? 'client-gBiyWvGNZcOh9lXCRQ6Y5PxvBvf3qem4'
      : 'client-reOL6zfU71P8bEMnzg5HlGpNE1wJRsL4';

    const amplitude = Amplitude?.getInstance();
    amplitude?.setUserId(proId.toString());

    const internExperiment = Experiment.initialize(AMPLITUDE_EXPERIMENT_JS_KEY, {
      exposureTrackingProvider: new SegmentExposureTrackingProvider(),
      debug,
      initialVariants: DefaultFlagValues,
      fetchTimeoutMillis: 3000,
      serverUrl: AMPLITUDE_ENDPOINT_URL,
    });

    startExperiment(internExperiment);

    return internExperiment;
  }, [proId, startExperiment, isDevelopment, debug]);

  const isVariantActive = createIsVariantActive(proId, experiment);
  const getVariantValue = createGetVariantValue(proId, experiment);
  const getPayloadVariant = createGetPayloadVariant(proId, experiment);

  const value: AmplitudeExperimentContextValue = {
    isVariantActive,
    getPayloadVariant,
    getVariantValue,
  };

  const LoadingComponent = () => (CustomLoader ? <CustomLoader /> : <Spinner show />);

  // Avoid children re-render if state provider is changed.
  return useMemo(
    () =>
      isInitialized ? (
        <AmplitudeExperimentContext.Provider value={value}>{children}</AmplitudeExperimentContext.Provider>
      ) : (
        <LoadingComponent />
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [children, proId, isInitialized]
  );
};

export { AmplitudeExperimentContext, AnonymousExperimentProvider };
